<script lang="ts">
    import { App } from "@/constants";
    import { STATUS } from "@/constants/statuses";
    import ReviewsTheme from "@apps/reviews/components/ReviewsTheme/ReviewsTheme.svelte";
    import { REVIEW_TYPE } from "@apps/reviews/constants";
    import ReviewsServiceBase from "@apps/reviews/services/ReviewsServiceBase";
    import ReviewsStorefrontValuesService from "@apps/reviews/services/ReviewsStorefrontValuesService";
    import {
        questionsFormModalToken,
        reviewsFormModalToken,
    } from "@apps/reviews/tokens";
    import GrowaveConfigurationEntity from "@lib/GrowaveConfigurationProvider/GrowaveConfigurationEntity";
    import { ProductIdManagerInterface } from "@lib/ProductIdManager/ProductIdManagerInterface";
    import { lastValueFrom } from "rxjs";
    import { onMount } from "svelte";

    import { ModalManager } from "@components/Modal/ModalManager";

    import { LEAVE_REVIEW_SOURCE } from "../review_form_modal_widget/constants";
    import ReviewsListHeader from "./components/ReviewsListHeader.svelte";
    import ReviewsListHeaderSkeleton from "./components/ReviewsListHeaderSkeleton.svelte";
    import ReviewsListInfoQuery from "./queries/ReviewsListInfoQuery";
    import ReviewsListInfoService from "./services/ReviewsListInfoService";

    export let reviewsStorefrontValuesService: ReviewsStorefrontValuesService;
    export let reviewsListInfoQuery: ReviewsListInfoQuery;
    export let reviewsListInfoService: ReviewsListInfoService;
    export let productIdManager: ProductIdManagerInterface;
    export let growaveConfiguration: GrowaveConfigurationEntity;
    export let addReviewButtonEnabled: boolean;
    export let addQuestionButtonEnabled: boolean;
    export let reviewsService: ReviewsServiceBase | undefined;
    export let modalManager: ModalManager;
    export let afterWidgetInject: () => void;

    const isReviewsAppEnabled = growaveConfiguration.availableApps.includes(
        App.Reviews
    );
    const isQuestionsAppEnabled = growaveConfiguration.availableApps.includes(
        App.Questions
    );

    const reviewsSettings$ =
        reviewsStorefrontValuesService.selectReviewsSettings();
    const reviewsBranding$ =
        reviewsStorefrontValuesService.selectReviewsBrandingOptions();

    const productId$ = productIdManager.productId$;

    const reviewsCount$ = reviewsListInfoQuery.totalCount$;
    const averageRating$ = reviewsListInfoQuery.averageRating$;
    const ratesInfo$ = reviewsListInfoQuery.ratesInfo$;
    const reviewsListInfoStatus$ = reviewsListInfoQuery.status$;

    const getReviewsListInfo = () => {
        if (!$productId$) {
            throw new Error("Product ID is not provided!");
        }

        lastValueFrom(
            reviewsListInfoService.getReviewsListInfo({
                productId: $productId$,
            })
        );
    };

    $: {
        $productId$;
        getReviewsListInfo();
    }

    const handleOpenReviewsForm = () => {
        modalManager.openModal(reviewsFormModalToken, true, {
            productId: $productId$ || undefined,
            source: LEAVE_REVIEW_SOURCE.REVIEWS_PRODUCT_PAGE_WIDGET,
            onLeaveReviewCb: (review) => {
                reviewsService?.appendReview(review);
            },
            reviewType: REVIEW_TYPE.PRODUCT_REVIEW,
        });
    };

    const handleOpenQuestionsForm = () => {
        if (!$productId$) {
            throw new Error("Product ID is not provided!");
        }

        modalManager.openModal(questionsFormModalToken, undefined, {
            productId: $productId$,
        });
    };

    onMount(() => afterWidgetInject());

    $: showListingHeaderSkeleton = [
        STATUS.VIRGIN,
        STATUS.INITIAL_LOADING,
    ].includes($reviewsListInfoStatus$);
</script>

{#if $reviewsSettings$ && $reviewsBranding$}
    <ReviewsTheme branding={$reviewsBranding$}>
        {#if showListingHeaderSkeleton}
            <ReviewsListHeaderSkeleton />
        {:else}
            <ReviewsListHeader
                reviewsCount={$reviewsCount$ || 0}
                averageRating={$averageRating$ || 0}
                ratesInfo={$ratesInfo$}
                reviewsListInfoStatus={$reviewsListInfoStatus$}
                {isQuestionsAppEnabled}
                {isReviewsAppEnabled}
                on:addReview={handleOpenReviewsForm}
                on:addQuestion={handleOpenQuestionsForm}
                {addReviewButtonEnabled}
                {addQuestionButtonEnabled}
            />
        {/if}
    </ReviewsTheme>
{/if}

<style lang="postcss" global>
    @import "../../../../themes/default/default.css";
    @import "../../../../themes/globals.css";
</style>
